import { evaluateExpression } from '../../core/expression';
import { formatPaise } from '../../core/money';

interface Props {
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  large?: boolean;
  currency?: 'INR' | 'USD';
}

export interface AmountState {
  paise: number | null;
  isExpression: boolean;
  message: string | null;
}

/**
 * Read an amount field's current state (FR-TXN-07 to FR-TXN-11).
 *
 * Exported so the form can decide whether Save is enabled from the same evaluation the
 * field displays — two evaluations could disagree about whether the input is valid.
 */
export function readAmount(value: string): AmountState {
  if (value.trim() === '') return { paise: null, isExpression: false, message: null };
  const result = evaluateExpression(value);
  if (!result.ok) return { paise: null, isExpression: false, message: result.message };
  return { paise: result.paise, isExpression: !result.literal, message: null };
}

/**
 * The amount field, with its result rendered live beneath it (WD-03).
 *
 * The parser is the one from M0: precedence-aware, no `eval()`, and the permitted
 * character set enforced at the tokeniser. Errors name the actual problem — "unbalanced
 * bracket", not "invalid" — because the user has to know which character to fix.
 */
export function AmountField({ value, onChange, autoFocus, large, currency = 'INR' }: Props) {
  const state = readAmount(value);
  const symbol = currency === 'INR' ? '₹' : '$';

  return (
    <div className={large ? 'amount-field large' : 'amount-field'}>
      <div className="amount-input">
        <span className="amount-symbol" aria-hidden="true">{symbol}</span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="decimal"
          autoFocus={autoFocus}
          aria-label="Amount"
          placeholder="0"
          aria-invalid={state.message !== null}
        />
      </div>
      <div className="amount-preview" aria-live="polite">
        {state.message ? (
          <span className="error">{state.message}</span>
        ) : state.isExpression && state.paise !== null ? (
          // Only shown for an actual expression — echoing "= ₹130.00" under a plain
          // "130" is noise.
          <span className="muted">= {formatPaise(state.paise, { currency })}</span>
        ) : (
          <span className="muted">&nbsp;</span>
        )}
      </div>
    </div>
  );
}
